/**
 * useSessionFlag — once-per-session boolean backed by sessionStorage (F7-P4 modal cap).
 *
 * Returns `[seen, markSeen]`. `seen` is true if the key was already set this session (so
 * the interstitial modal shows once per visitor session, not per page-load). Mirrors the
 * try/catch sessionStorage shape used by useGeolocation. Safe when storage is unavailable
 * (treats as not-seen but markSeen is a no-op — degrades to "may show again", never throws).
 */
import { useCallback, useRef, useState } from 'react';

export function useSessionFlag(key: string): [boolean, () => void] {
  const [seen, setSeen] = useState<boolean>(() => {
    try {
      return sessionStorage.getItem(key) === '1';
    } catch {
      return false;
    }
  });
  const keyRef = useRef(key);
  keyRef.current = key;

  const markSeen = useCallback(() => {
    setSeen(true);
    try {
      sessionStorage.setItem(keyRef.current, '1');
    } catch {
      // sessionStorage unavailable (private mode / disabled) — non-fatal; the in-memory
      // `seen` still suppresses re-show within this mount.
    }
  }, []);

  return [seen, markSeen];
}
